/* Napurel section behaviour: FAQ accordion, review filter, size pickers outside the buy box,
   smooth anchors, reveal motion. Loaded with defer by the sections that need it; runs once. */
(function () {
  if (window.__ppSectionsLoaded) return;
  window.__ppSectionsLoaded = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollToEl(el, offset = 16) {
    if (!el) return false;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: reduceMotion() ? 'auto' : 'smooth' });
    return true;
  }

  /* ---------- FAQ accordion (one open at a time, grid-rows animation) ---------- */
  if (!customElements.get('pp-accordion')) {
    class PpAccordion extends HTMLElement {
      connectedCallback() {
        this.single = this.dataset.single !== 'false';
        this.items = Array.from(this.querySelectorAll('details'));
        this.items.forEach((item) => {
          item.querySelector('summary').addEventListener('click', (event) => {
            event.preventDefault();
            if (item.open) this.close(item);
            else this.open(item);
          });
        });
      }

      open(item) {
        if (this.single) this.items.forEach((other) => other !== item && other.open && this.close(other));
        item.classList.add('is-opening');
        item.open = true;
        requestAnimationFrame(() => requestAnimationFrame(() => item.classList.remove('is-opening')));
      }

      close(item) {
        if (reduceMotion()) {
          item.open = false;
          return;
        }
        item.classList.add('is-closing');
        setTimeout(() => {
          item.open = false;
          item.classList.remove('is-closing');
        }, 250);
      }
    }
    customElements.define('pp-accordion', PpAccordion);
  }

  /* ---------- Swipe carousel (mobile benefits): active slide, progress bar, counter ---------- */
  if (!customElements.get('pp-carousel')) {
    class PpCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-pp-track]');
        this.slides = Array.from(this.querySelectorAll('[data-pp-slide]'));
        this.bar = this.querySelector('[data-pp-bar]');
        this.current = this.querySelector('[data-pp-current]');
        if (!this.track || this.slides.length < 2) return;
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        // The dimmed "not in focus" state starts only after the entrance animation (reveal) has played.
        const arm = () => {
          if (this.armed || this.track.classList.contains('pp-reveal--pending')) return;
          this.armed = true;
          setTimeout(() => this.track.classList.add('is-ready'), 600 + this.slides.length * 110);
        };
        new MutationObserver(arm).observe(this.track, { attributes: true, attributeFilter: ['class'] });
        setTimeout(arm, 300);
        this.update();
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const progress = max > 0 ? this.track.scrollLeft / max : 0;
        const index = Math.round(progress * (this.slides.length - 1));
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        if (this.current) this.current.textContent = index + 1;
        if (this.bar) this.bar.style.left = `${progress * (100 - parseFloat(this.bar.style.width))}%`;
      }
    }
    customElements.define('pp-carousel', PpCarousel);
  }

  /* ---------- Napurel vs. one alternative (mobile comparison) ---------- */
  if (!customElements.get('pp-versus')) {
    class PpVersus extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-col].pp-seg__btn'));
        this.buttons.forEach((button) =>
          button.addEventListener('click', () => {
            if (this.dataset.active === button.dataset.col) return;
            this.dataset.active = button.dataset.col;
            this.buttons.forEach((b) => {
              const on = b === button;
              b.classList.toggle('is-selected', on);
              b.setAttribute('aria-pressed', String(on));
            });
            this.classList.remove('is-switching');
            void this.offsetWidth; // restart the swap animation
            this.classList.add('is-switching');
          })
        );
      }
    }
    customElements.define('pp-versus', PpVersus);
  }

  /* ---------- Steps timeline (mobile): rail fills with scroll, dots switch on, steps reveal on arrival ---------- */
  if (!customElements.get('pp-timeline')) {
    class PpTimeline extends HTMLElement {
      connectedCallback() {
        this.fill = this.querySelector('[data-pp-rail-fill]');
        this.steps = Array.from(this.querySelectorAll('[data-pp-step]'));
        this.mq = window.matchMedia('(max-width: 749px)');
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        if (!reduceMotion() && !window.Shopify?.designMode) this.classList.add('is-live');
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      update() {
        if (!this.mq.matches) {
          this.steps.forEach((step) => step.classList.add('is-seen'));
          return;
        }
        const line = window.innerHeight * 0.62;
        const rect = this.getBoundingClientRect();
        // Rail runs from the first dot to the last one
        const rail = this.querySelector('.pp-steps__rail');
        const lastDot = this.steps.length && this.steps[this.steps.length - 1].querySelector('.pp-step__dot');
        if (rail && lastDot) {
          const end = lastDot.getBoundingClientRect();
          rail.style.bottom = `${Math.max(0, rect.bottom - (end.top + end.height / 2))}px`;
        }
        const progress = Math.min(1, Math.max(0, (line - rect.top) / Math.max(1, rect.height)));
        if (this.fill) this.fill.style.setProperty('--pp-rail', progress.toFixed(3));
        this.steps.forEach((step) => {
          const dot = step.querySelector('.pp-step__dot') || step;
          const top = dot.getBoundingClientRect().top;
          const on = top < line;
          step.classList.toggle('is-on', on);
          if (top < window.innerHeight * 0.9) step.classList.add('is-seen');
        });
      }
    }
    customElements.define('pp-timeline', PpTimeline);
  }

  /* ---------- Review filter ---------- */
  if (!customElements.get('pp-review-filter')) {
    class PpReviewFilter extends HTMLElement {
      connectedCallback() {
        this.chips = Array.from(this.querySelectorAll('[data-filter]'));
        this.items = Array.from(this.querySelectorAll('[data-tag]'));
        this.chips.forEach((chip) => chip.addEventListener('click', () => this.apply(chip)));
      }

      apply(chip) {
        const filter = chip.dataset.filter;
        this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        this.items.forEach((item) => {
          item.hidden = filter !== 'all' && item.dataset.tag !== filter;
        });
      }
    }
    customElements.define('pp-review-filter', PpReviewFilter);
  }

  /* ---------- Size selection shared by buy box, size table and final call ---------- */
  const buyBox = () => document.getElementById('pp-buybox') || document.querySelector('product-info');

  function currentSize() {
    const checked = document.querySelector('[data-pp-size-picker] input:checked');
    return checked ? checked.value : null;
  }

  function syncSizeUi(override) {
    const size = currentSize() || override;
    if (!size) return;
    document.querySelectorAll('[data-pp-size-table] [data-size]').forEach((cell) => {
      cell.classList.toggle('is-selected', cell.dataset.size === size);
    });
    document.querySelectorAll('[data-pp-size-table] [data-pp-pick-size], [data-pp-offer-recap] [data-pp-pick-size]').forEach((btn) => {
      const on = btn.dataset.ppPickSize === size;
      btn.setAttribute('aria-pressed', String(on));
      btn.classList.toggle('is-selected', on);
    });
  }

  /* Dawn's product-info.js focuses the buy-box option after re-rendering, which scrolls the page up.
     While a size is picked from elsewhere on the page, focus() runs with preventScroll so the page stays put. */
  let keepScrollUntil = 0;
  const nativeFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (options) {
    if (Date.now() < keepScrollUntil && this.closest && this.closest('variant-selects')) {
      return nativeFocus.call(this, { ...(options || {}), preventScroll: true });
    }
    return nativeFocus.call(this, options);
  };

  // Dawn's media-gallery.js also scrolls the window up to the variant's image after a variant change.
  // While a size is picked from the size table/tabs, window scrolls that move the page up are ignored.
  const nativeWindowScrollTo = window.scrollTo.bind(window);
  window.scrollTo = function (x, y) {
    if (Date.now() < keepScrollUntil) {
      const top = typeof x === 'object' && x !== null ? x.top : y;
      if (typeof top === 'number' && top < window.scrollY - 1) return;
    }
    return nativeWindowScrollTo(x, y);
  };

  function pickSize(size, keepScroll) {
    const input = Array.from(document.querySelectorAll('[data-pp-size-picker] input')).find((el) => el.value === size);
    if (input && !input.checked) {
      if (keepScroll) keepScrollUntil = Date.now() + 4000;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncSizeUi(size);
  }

  document.addEventListener('change', (event) => {
    if (event.target.closest && event.target.closest('[data-pp-size-picker]')) syncSizeUi();
  });

  document.addEventListener('click', (event) => {
    const pick = event.target.closest('[data-pp-pick-size]');
    if (pick) {
      const toBuyBox = !!pick.closest('[data-pp-offer-recap]');
      pickSize(pick.dataset.ppPickSize, !toBuyBox);
      if (toBuyBox) scrollToEl(buyBox(), 0);
      return;
    }

    const scrollLink = event.target.closest('[data-pp-scroll-buybox]');
    if (scrollLink) {
      const href = scrollLink.getAttribute('href') || '';
      if ((href === '#pp-buybox' || href === '') && scrollToEl(buyBox(), 0)) event.preventDefault();
      return;
    }

    const anchor = event.target.closest('a[href^="#pp-"]');
    if (anchor) {
      const target = document.getElementById(anchor.getAttribute('href').slice(1));
      if (target && scrollToEl(target)) event.preventDefault();
    }
  });

  function initSubscriptions() {
    syncSizeUi();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(syncSizeUi));
    }
  }

  /* ---------- "Read more" for clamped text (mobile story) ---------- */
  function initClamp() {
    document.querySelectorAll('[data-pp-clamp]').forEach((body) => {
      const button = body.parentElement.querySelector('[data-pp-more]');
      if (!button) return;
      const label = button.querySelector('span');
      const more = label.textContent;
      const measure = () => {
        if (button.getAttribute('aria-expanded') === 'true') return;
        body.classList.add('is-clamped');
        const clamped = body.scrollHeight - body.clientHeight > 2;
        if (!clamped) body.classList.remove('is-clamped');
        button.hidden = !clamped;
      };
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        body.classList.toggle('is-clamped', !open);
        label.textContent = open ? button.dataset.less || more : more;
      });
      measure();
      window.addEventListener('resize', measure, { passive: true });
    });
  }

  /* ---------- Reveal motion (only adds the hidden state when motion is allowed and JS runs) ---------- */
  function initReveal() {
    if (reduceMotion() || !('IntersectionObserver' in window) || window.Shopify?.designMode) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('pp-reveal--pending');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.pp-reveal').forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('pp-reveal--pending');
        observer.observe(el);
      }
    });
  }

  function init() {
    initClamp();
    initSubscriptions();
    initReveal();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
